import { effectiveLayoutSidebarBlocks } from '@/common/preferences/layoutSidebarBlocks'
import { useApi } from '@/common/state/stores/apiStore'
import { logHandledApiError } from '@/common/utils/formatApiError'
import { EventBus } from '@/common/utils/eventBus'
import { Preferences } from '@/shared/api'
import { defineStore } from 'pinia'
import { preferenceStorage } from '../preferenceStorage'
import { findCircularReference } from '../utils'

export interface LocalPreferences {
  keybindings: {
    [key: string]: string
  }
  apiEnvironment: 'production' | 'development'
  openWindowAtLogin: boolean
  themeMode: 'light' | 'dark' | 'system'
  preferredLightThemeId: string
  preferredDarkThemeId: string
  planMyDayNotificationEnabled: boolean
  planMyDayNotificationTime: string // HH:mm format, e.g. "08:30"
  planMyDayNotificationWeekendsEnabled: boolean
  /**
   * Seeded to `false` on desktop only (desktopPreferences.ts) to drive the
   * one-time themeMode -> 'system' migration in `loadState`. Declared here
   * rather than as a module augmentation in desktopPreferences.ts because the
   * migration that reads it lives in this file -- src/common must not depend on
   * a field that only exists once a src/desktop module has been loaded. Optional,
   * so it stays `undefined` -- and the migration stays a no-op -- on web/mobile.
   */
  themeSystemMigrationDone?: boolean
}

// Define the initial state separately to reuse it
const initialLocalPreferencesState = {
  keybindings: {},
  apiEnvironment: 'production',
  openWindowAtLogin: true,
  themeMode: 'dark',
  preferredLightThemeId: 'zen',
  preferredDarkThemeId: 'shadow',
  planMyDayNotificationEnabled: false,
  planMyDayNotificationTime: '08:00', // Default to 8:00 AM
  planMyDayNotificationWeekendsEnabled: false // Default to off for weekends
}

let combinedInitialState = { ...initialLocalPreferencesState }

export function extendInitialLocalPreferences(extension: Record<string, any>) {
  combinedInitialState = { ...combinedInitialState, ...extension }
}

export const usePreferenceStore = defineStore('preferences', {
  state: () => ({
    preferences: {} as Preferences,
    lastKnownGoodPreferences: {} as Preferences, // Backup of last known good values from server
    localPreferences: { ...combinedInitialState } as LocalPreferences, // Use the combined state
    initialized: false,
    _preferenceUpdateHandler: null as (() => void) | null,
    _lastResyncAt: 0 as number
  }),

  actions: {
    async loadState(resync = true) {
      console.debug('Loading preference state with resync', resync)
      // Load preferences from IPC store first
      const storedPreferences = await preferenceStorage.get('preferences')
      const storedLocalPreferences = await preferenceStorage.get('localPreferences')
      if (storedPreferences) {
        this.preferences = {
          ...storedPreferences,
          layout_sidebar_blocks: effectiveLayoutSidebarBlocks(
            storedPreferences.layout_sidebar_blocks
          )
        }
        // Initialize backup with loaded preferences
        this.lastKnownGoodPreferences = { ...this.preferences }
      }
      if (storedLocalPreferences) {
        this.localPreferences = {
          ...combinedInitialState,
          ...storedLocalPreferences
        }
      } else {
        // No stored preferences, ensure we use the combined initial state (with extensions)
        this.localPreferences = { ...combinedInitialState } as LocalPreferences
      }

      // Migrate old theme IDs to new ones
      let migrationOccurred = false
      if (this.localPreferences.preferredLightThemeId === 'nocal-light') {
        this.localPreferences.preferredLightThemeId = 'zen'
        migrationOccurred = true
      }
      if (this.localPreferences.preferredDarkThemeId === 'nocal-dark') {
        this.localPreferences.preferredDarkThemeId = 'shadow'
        migrationOccurred = true
      }

      // One-time desktop migration onto the new 'system' default (follow the OS).
      // NOTE: this canNOT distinguish "still on the old hardcoded 'dark' default"
      // from "deliberately chose Dark" — no such intent flag was ever stored — so
      // it flips EVERY desktop install whose themeMode is 'dark' to 'system', once,
      // including users who explicitly picked Dark. That's a conscious, recoverable
      // tradeoff: 'system' still renders dark while the OS is dark, and they can
      // re-select Dark. The flag is only seeded on desktop (desktopPreferences.ts),
      // so this is a no-op on web/mobile. After it runs the flag is true, so any
      // later explicit choice sticks.
      if (this.localPreferences.themeSystemMigrationDone === false) {
        if (this.localPreferences.themeMode === 'dark') {
          this.localPreferences.themeMode = 'system'
        }
        this.localPreferences.themeSystemMigrationDone = true
        migrationOccurred = true
      }

      // Save migrated preferences if any changes were made
      if (migrationOccurred) {
        await this.saveState(false) // Don't emit event to avoid infinite loop
      }

      // Check if openWindowAtLogin is undefined and set a default if so
      if (typeof this.localPreferences.openWindowAtLogin === 'undefined') {
        this.localPreferences.openWindowAtLogin = true
      }

      // Then fetch from API to ensure we have latest
      if (resync) {
        await this.resyncPreferences(false)
      }
      this.initialized = true
    },

    async subscribeToUpdates() {
      if (!this._preferenceUpdateHandler) {
        this._preferenceUpdateHandler = () => this.loadState(false)
        console.debug('Subscribing to preference updates')
        EventBus.on('preference-storage-updated', this._preferenceUpdateHandler)
      }
    },

    async unsubscribeFromUpdates() {
      if (this._preferenceUpdateHandler) {
        console.debug('Unsubscribing from preference updates')
        EventBus.off('preference-storage-updated', this._preferenceUpdateHandler)
        this._preferenceUpdateHandler = null
      }
    },

    async saveLocalPreference<K extends keyof LocalPreferences>(
      key: K,
      value: LocalPreferences[K]
    ) {
      ;(this.localPreferences as LocalPreferences)[key] = value
      await this.saveState()
    },

    async saveState(emitEvent = true) {
      await preferenceStorage.set(
        'preferences',
        JSON.parse(JSON.stringify(this.preferences, findCircularReference()))
      )
      await preferenceStorage.set(
        'localPreferences',
        JSON.parse(JSON.stringify(this.localPreferences, findCircularReference()))
      )

      EventBus.emit('preference-storage-updated', {}, 'other')
    },

    async resyncPreferences(emitEvent = true) {
      const now = Date.now()
      const RESYNC_DEBOUNCE_MS = 2000
      if (this._lastResyncAt && now - this._lastResyncAt < RESYNC_DEBOUNCE_MS) {
        return
      }
      this._lastResyncAt = now
      const timestamp = new Date().getTime()
      try {
        const prefs = (await useApi().me().mePreferencesRetrieve({ params: { timestamp } })).data
        prefs.layout_sidebar_blocks = effectiveLayoutSidebarBlocks(prefs.layout_sidebar_blocks)
        this.preferences = prefs
        // Update backup of last known good values
        this.lastKnownGoodPreferences = { ...this.preferences }
        await this.saveState(emitEvent)
      } catch (error) {
        // Best-effort resync: on failure keep the cached preferences. Log via
        // the shared helper so a 401 or transient network drop stays a warn
        // (not a Sentry event via captureConsoleIntegration) and the raw
        // AxiosError is never logged. Deliberately not rethrown — no caller has
        // a recovery beyond keeping current prefs, and rethrowing left this as
        // an unhandled rejection and stopped loadState from finishing startup
        // init when offline (DESKTOP-2M).
        logHandledApiError('Failed to resync preferences:', error)
      }
    },

    async setThemeMode(mode: 'light' | 'dark' | 'system') {
      await this.saveLocalPreference('themeMode', mode)
    },

    async setPreferredLightTheme(themeId: string) {
      await this.saveLocalPreference('preferredLightThemeId', themeId)
    },

    async setPreferredDarkTheme(themeId: string) {
      await this.saveLocalPreference('preferredDarkThemeId', themeId)
    },

    async clearLocalPreferences() {
      await preferenceStorage.delete('localPreferences')
      // Reset the state to initial defaults
      this.localPreferences = { ...combinedInitialState } as LocalPreferences
    }
  }
})
